import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SearchRow } from "../MarketTrad/shared";
import NlimsDataTable from "../shared/NlimsDataTable/NlimsDataTable";
import { useCountUp } from "../../hooks/useCountUp";
import WorkforceCreateModal from "./WorkforceCreateModal";
import { useAuth } from "../../context/AuthContext";
import { canCreateModule, MODULE_KEYS } from "../../config/rbacConfig";
import { livestockActorsApi } from "../../api/workforceApi";
import { filterOptions } from "./mockData/WorkData";
import { ActionDropdown, anchorRect, QuickConfirmModal, actionCellHtml, WORKFLOW_RULES } from "../shared/RowActionMenu";
import useRowWorkflowActions from "../shared/useRowWorkflowActions";

function parseStatValue(raw) {
  const s = String(raw).trim();
  const kMatch = s.match(/^([\d.]+)([kK])$/);
  if (kMatch) {
    const dec = (kMatch[1].split(".")[1] || "").length;
    const end = Math.round(parseFloat(kMatch[1]) * 1000);
    return { end, format: (n) => `${(n / 1000).toFixed(dec)}${kMatch[2]}` };
  }
  const numMatch = s.match(/^[\d,]+$/);
  if (numMatch) return { end: parseInt(s.replace(/,/g, ""), 10), format: (n) => n.toLocaleString() };
  return { end: 0, format: () => s };
}

function StatCard({ label, value, sub }) {
  const { end, format } = parseStatValue(value);
  const { count, ref } = useCountUp(end);
  return (
    <div ref={ref} className="min-w-0 rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-[11px] font-semibold leading-tight text-gray-700">{label}</p>
      <p className="text-3xl font-black leading-none text-gray-900">{end > 0 ? format(count) : value}</p>
      <p className="mt-0.5 text-[10px] leading-snug text-gray-400">{sub}</p>
    </div>
  );
}

const STATUS_STYLE = {
  Active: "background:#dcfce7;color:#15803d;",
  Inactive: "background:#fee2e2;color:#dc2626;",
  Suspended: "background:#fef3c7;color:#b45309;",
  "On Leave": "background:#dbeafe;color:#1d4ed8;",
  Completed: "background:#dcfce7;color:#15803d;",
  Pending: "background:#fef3c7;color:#b45309;border:1px solid #fcd34d;",
  Rejected: "background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;",
  Verified: "background:#dcfce7;color:#15803d;border:1px solid #86efac;",
};

function statusHtml(data, type) {
  if (type !== "display") return data ?? "";
  const style = STATUS_STYLE[data] || "background:#f3f4f6;color:#6b7280;";
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;${style}">${data || "-"}</span>`;
}

const ACTOR_TYPE_OPTS = filterOptions.actorTypes.filter((t) => t !== "All Actor Types");

const COLUMNS = [
  { title: "Full Name", data: "fullName", sortable: true },
  { title: "Actor Type", data: "actorType", sortable: true },
  { title: "Sex", data: "sex", sortable: true },
  { title: "Age Group", data: "ageGroup", sortable: true },
  { title: "Registration No.", data: "registrationNumber", sortable: true, defaultContent: "" },
  { title: "Phone", data: "phone", sortable: true, defaultContent: "" },
  { title: "State", data: "state", sortable: true },
  { title: "LGA", data: "lga", sortable: true },
  { title: "Status", data: "status", sortable: true, render: statusHtml },
  { title: "Verification Status", data: "verificationStatus", sortable: true, render: statusHtml },
  { title: "Action", data: null, sortable: false, render: () => actionCellHtml() },
];

function countBy(records, predicate) {
  return records.reduce((total, record) => total + (predicate(record) ? 1 : 0), 0);
}

function normalizeActorFilters(query = {}) {
  return {
    per_page: 200,
    actor_type: query.actor_type || undefined,
    status: query.status || undefined,
    verification_status: query.verification_status || undefined,
    sex: query.sex || undefined,
    age_group: query.age_group || undefined,
    state: query.state || undefined,
    lga: query.lga || undefined,
    q: query.q || undefined,
  };
}

export default function LivestockActors() {
  const navigate = useNavigate();
  const { user, permissions = [] } = useAuth();
  const roleId = user?.roleId ?? "";
  const isR05 = roleId === "R05";
  const canCreateRecord = canCreateModule(permissions, MODULE_KEYS.WORKFORCE) && roleId !== "R02" && roleId !== "R06";
  const canEditRecord = roleId !== "R02" && roleId !== "R06";
  const canDeleteRecord = roleId === "R01" || roleId === "R04" || roleId === "R05";

  const [ownData, setOwnData] = useState([]);
  const [selectedActorType, setSelectedActorType] = useState("");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMenu, setActionMenu] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const loadActors = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await livestockActorsApi.listAll(
        normalizeActorFilters({
          actor_type: selectedActorType ? selectedActorType.toLowerCase().replace(/\s+/g, "_") : "",
          // LGA Field Officers (R05) only ever see actors within their own
          // assigned state/LGA, matching the scope their Create form already locks to.
          state: isR05 ? (user?.assignedState || "") : "",
          lga: isR05 ? (user?.assignedLga || "") : "",
        })
      );
      const items = payload.items || [];
      const scoped = isR05
        ? items.filter(
            (row) =>
              String(row.state ?? "").toLowerCase() === String(user?.assignedState ?? "").toLowerCase() &&
              String(row.lga ?? "").toLowerCase() === String(user?.assignedLga ?? "").toLowerCase()
          )
        : items;
      setOwnData(scoped);
    } catch (err) {
      setOwnData([]);
      setError(err?.response?.data?.message || err?.message || "Failed to load livestock actors.");
    } finally {
      setLoading(false);
    }
  }, [selectedActorType, isR05, user?.assignedState, user?.assignedLga]);

  useEffect(() => {
    loadActors();
  }, [loadActors]);

  const stats = useMemo(() => {
    const total = ownData.length;
    const active = countBy(ownData, (row) => String(row.status).toLowerCase() === "active");
    const veterinarians = countBy(ownData, (row) => String(row.actorType).toLowerCase() === "veterinarian");
    const animalScientists = countBy(ownData, (row) => String(row.actorType).toLowerCase() === "animal scientist");
    const extensionTeams = countBy(ownData, (row) => {
      const value = String(row.actorType).toLowerCase();
      return value === "extension worker" || value === "cahw" || value === "vet paraprofessional";
    });
    const farmerProducers = countBy(ownData, (row) => String(row.actorType).toLowerCase() === "farmer producer");

    return [
      { label: "Total Registered Actors", value: String(total), sub: "Total number of actors registered" },
      { label: "Active vs. Inactive", value: String(active), sub: `Active registered actors of ${total.toLocaleString() || 0}` },
      { label: "Veterinarians", value: String(veterinarians), sub: "Active registered veterinarians" },
      { label: "Animal Scientists", value: String(animalScientists), sub: "Active registered animal scientists" },
      { label: "Extension Officers & CAHWs", value: String(extensionTeams), sub: "Extension workers, CAHWs and paraprofessionals" },
      { label: "Farmer / Producers", value: String(farmerProducers), sub: "Active registered farmer / producers" },
    ];
  }, [ownData]);

  const handleRowAction = useCallback((action, row, meta) => {
    const rect = anchorRect(meta?.button);
    if (action === "menu" || action == null) setActionMenu({ row, rect });
  }, []);

  const workflow = useRowWorkflowActions({
    api: livestockActorsApi,
    rules: WORKFLOW_RULES.livestockActors,
    moduleKey: "workforce",
    roleId,
    onChanged: loadActors,
    onError: setError,
    labelOf: (row) => row?.fullName || row?.full_name,
  });

  const handleDelete = useCallback(async (row) => {
    if (!canDeleteRecord) return;
    try {
      const uuid = row?.uuid || row?.id;
      if (!uuid) throw new Error("Missing livestock actor uuid.");
      await livestockActorsApi.remove(uuid);
      await loadActors();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to delete livestock actor.");
    }
  }, [canDeleteRecord, loadActors]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try { await handleDelete(deleteTarget); setDeleteTarget(null); }
    finally { setDeleteBusy(false); }
  }, [deleteTarget, handleDelete]);

  return (
    <div>
      {!isR05 && (
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <span className="whitespace-nowrap text-sm font-semibold text-gray-700">Filter by Actor Type:</span>
          <div className="relative">
            <select
              value={selectedActorType}
              onChange={(e) => setSelectedActorType(e.target.value)}
              className="cursor-pointer appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-green-500 min-w-[200px]"
            >
              <option value="">All Actor Types</option>
              {ACTOR_TYPE_OPTS.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
            <svg
              className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {selectedActorType && (
            <span className="text-sm text-gray-500">
              Showing: <span className="font-semibold text-green-700">{selectedActorType}</span>
              <button onClick={() => setSelectedActorType("")} className="ml-2 text-xs font-bold text-gray-400 hover:text-gray-600">
                x Clear
              </button>
            </span>
          )}
        </div>
      )}

      <SearchRow
        searchValue={search}
        onSearch={setSearch}
        onCreate={() => navigate("/workforce-institutional/new?subModule=actors")}
        canCreate={canCreateRecord}
      />

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {!isR05 && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {stats.map((stat, index) => <StatCard key={index} {...stat} />)}
        </div>
      )}

      <NlimsDataTable
        tableId="livestock-actors-table"
        columns={COLUMNS}
        data={ownData}
        filters={{ actorType: selectedActorType }}
        serverSide={false}
        hideSearch
        searchQuery={search}
        pageLength={10}
        onRowAction={handleRowAction}
        enableRowSelection
      />

      {loading && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
          Loading livestock actors...
        </div>
      )}

      {workflow.busy && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">{workflow.busy}</div>
      )}

      {actionMenu && !deleteBusy && !workflow.busy && (
        <ActionDropdown
          row={actionMenu.row}
          rect={actionMenu.rect}
          roleId={roleId}
          rules={WORKFLOW_RULES.livestockActors}
          onClose={() => setActionMenu(null)}
          onView={() => { navigate(`/workforce-institutional/view/${actionMenu.row.uuid}?subModule=actors`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onEdit={() => { navigate(`/workforce-institutional/edit/${actionMenu.row.uuid}?subModule=actors`, { state: { row: actionMenu.row } }); setActionMenu(null); }}
          onSubmit={() => workflow.run(actionMenu.row, "submitted")}
          onValidate={() => workflow.run(actionMenu.row, "validated")}
          onApprove={() => workflow.run(actionMenu.row, "approved")}
          onReject={() => { workflow.askReject(actionMenu.row); setActionMenu(null); }}
          onDelete={() => { setDeleteTarget(actionMenu.row); setActionMenu(null); }}
        />
      )}
      {workflow.rejectModal}
      {deleteTarget && (
        <QuickConfirmModal
          title="Delete Actor"
          message={`Permanently delete "${deleteTarget.fullName || 'this actor'}"? This cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onClose={() => setDeleteTarget(null)}
          busy={deleteBusy}
        />
      )}

      <WorkforceCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={loadActors}
        defaultSubModule="actors"
      />
    </div>
  );
}

